import { Field, Input, Label } from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import {
  convertSingleDigitDay,
  convertSingleDigitMonth,
  convertSingleDigitYear,
  convertThreeDigitYear,
  convertTwoDigitYear,
  getYearConstraints,
} from "../../utils";

/**
 * Props interface for the DateOfBirthField component.
 */
interface DateOfBirthFieldProps {
  /** The name of the field for React Hook Form */
  name: string;
  /** Optional error message to display */
  error?: string | undefined;
  /** Optional class names for styling */
  className?: string;
}

/**
 * A date of birth field component with separate day, month, and year input fields.
 * Designed to work with React Hook Form using useController.
 *
 * Features:
 * - Three input fields for day, month, and year.
 * - Validation for each field and overall date validity.
 * - Integration with React Hook Form for seamless form management.
 * - Accessibility with Headless UI components.
 * - Responsive design with Tailwind CSS.
 *
 * @example
 * ```tsx
 * <DateOfBirthField name="date_of_birth" error={errors.date_of_birth?.message} />
 * ```
 */
export default function DateOfBirthField({ name, error, className }: DateOfBirthFieldProps) {
  const { control } = useFormContext();
  const {
    field: { onChange, value: controllerValue },
  } = useController({
    name,
    control,
  });

  const [day, setDay] = useState<string>(() => {
    if (typeof controllerValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(controllerValue)) {
      return controllerValue.split("-")[2] ?? "";
    }
    return "";
  });
  const [month, setMonth] = useState<string>(() => {
    if (typeof controllerValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(controllerValue)) {
      return controllerValue.split("-")[1] ?? "";
    }
    return "";
  });
  const [year, setYear] = useState<string>(() => {
    if (typeof controllerValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(controllerValue)) {
      return controllerValue.split("-")[0] ?? "";
    }
    return "";
  });
  const [focusedField, setFocusedField] = useState<"day" | "month" | "year" | null>(null);
  const dayInputRef = useRef<HTMLInputElement | null>(null);
  const monthInputRef = useRef<HTMLInputElement | null>(null);
  const yearInputRef = useRef<HTMLInputElement | null>(null);

  // Get dynamic year constraints
  const { minYear, maxYear } = getYearConstraints();

  /**
   * Handles day input changes
   */
  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setDay("");
      return;
    }
    // Keep as string to preserve leading zeros and length
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length <= 2) {
      setDay(numericValue);
      if (numericValue.length === 2) {
        requestAnimationFrame(() => {
          monthInputRef.current?.focus();
        });
      }
    }
  };

  /**
   * Handles month input changes
   */
  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setMonth("");
      return;
    }
    // Keep as string to preserve leading zeros and length
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length <= 2) {
      setMonth(numericValue);
      if (numericValue.length === 2) {
        requestAnimationFrame(() => {
          yearInputRef.current?.focus();
        });
      }
    }
  };

  /**
   * Handles year input changes
   */
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setYear("");
      return;
    }
    // Keep as string to preserve leading zeros and length
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length <= 4) {
      setYear(numericValue);
    }
  };

  /**
   * Handles day input blur - converts single digit days to 2-digit days
   */
  const handleDayBlur = () => {
    setFocusedField(null);

    if (day && day.length === 1) {
      const twoDigitDay = convertSingleDigitDay(day);
      setDay(twoDigitDay);
      // Trigger onChange with the converted day
      onChange(`${year}-${String(month).padStart(2, "0")}-${twoDigitDay}`);
    }
  };

  /**
   * Handles month input blur - converts single digit months to 2-digit months
   */
  const handleMonthBlur = () => {
    setFocusedField(null);

    if (month && month.length === 1) {
      const twoDigitMonth = convertSingleDigitMonth(month);
      setMonth(twoDigitMonth);
      // Trigger onChange with the converted month
      onChange(`${year}-${twoDigitMonth}-${String(day).padStart(2, "0")}`);
    }
  };

  /**
   * Handles year input blur - converts single/2-digit years to 4-digit years
   */
  const handleYearBlur = () => {
    setFocusedField(null);

    if (year && year.length === 1) {
      const fourDigitYear = convertSingleDigitYear(year);
      setYear(fourDigitYear);
      onChange(fourDigitYear);
    }
    if (year && year.length === 2) {
      const fourDigitYear = convertTwoDigitYear(year);
      setYear(fourDigitYear);
      onChange(fourDigitYear);
    }
    if (year && year.length === 3) {
      const fourDigitYear = convertThreeDigitYear(year);
      setYear(fourDigitYear);
      onChange(fourDigitYear);
    }
  };

  /**
   * Moves focus forward on Enter
   */
  const handleEnterNavigation = (e: React.KeyboardEvent<HTMLInputElement>, currentField: "day" | "month" | "year") => {
    if (e.key !== "Enter") {
      return;
    }

    e.preventDefault();
    if (currentField === "day") {
      monthInputRef.current?.focus();
      return;
    }

    if (currentField === "month") {
      yearInputRef.current?.focus();
    }
  };

  /**
   * Moves focus backward on delete/backspace from empty field
   */
  const handleDeleteNavigation = (e: React.KeyboardEvent<HTMLInputElement>, currentField: "day" | "month" | "year") => {
    const isDeleteKey = e.key === "Backspace" || e.key === "Delete";
    if (!isDeleteKey) {
      return;
    }

    if (currentField === "year" && year === "") {
      e.preventDefault();
      monthInputRef.current?.focus();
      return;
    }

    if (currentField === "month" && month === "") {
      e.preventDefault();
      dayInputRef.current?.focus();
    }
  };

  /**
   * Handles keyboard navigation across day/month/year fields
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentField: "day" | "month" | "year") => {
    handleEnterNavigation(e, currentField);
    handleDeleteNavigation(e, currentField);
  };

  useEffect(() => {
    if (day !== "" && month !== "" && year !== "") {
      // Pass date as string to Zod for validation
      const dateString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      onChange(dateString);
    } else {
      // For empty fields, set undefined to trigger validation
      onChange(undefined);
    }
  }, [day, month, year, onChange]);

  return (
    <Field className={clsx("relative", className)}>
      {/* Error message with proper spacing */}
      {error && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 font-medium" data-testid="date-of-birth-field-error">
            {error}
          </p>
        </div>
      )}
      <div className="flex min-w-0 gap-2">
        {/* Day input */}
        <div className="relative min-w-0 flex-1">
          <Label className="block text-sm font-semibold text-gray-700 mb-2">Day</Label>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min="1"
            max="31"
            maxLength={2}
            enterKeyHint="next"
            value={day || ""}
            onChange={handleDayChange}
            onFocus={() => setFocusedField("day")}
            onBlur={handleDayBlur}
            onKeyDown={(e) => handleKeyDown(e, "day")}
            placeholder="DD"
            className={clsx(
              "w-full px-3 py-3 border-2 rounded-lg bg-white text-gray-600 font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-center text-lg",
              {
                "border-red-500": error,
                "border-gray-300": !error,
                "border-pink-500": focusedField === "day",
              }
            )}
            data-testid="day-input"
            ref={dayInputRef}
          />
        </div>

        {/* Month input */}
        <div className="relative min-w-0 flex-1">
          <Label className="block text-sm font-semibold text-gray-700 mb-2">Month</Label>
          <Input
            type="text"
            inputMode="numeric"
            min="1"
            max="12"
            maxLength={2}
            enterKeyHint="next"
            value={month || ""}
            onChange={handleMonthChange}
            onFocus={() => setFocusedField("month")}
            onBlur={handleMonthBlur}
            onKeyDown={(e) => handleKeyDown(e, "month")}
            placeholder="MM"
            className={clsx(
              "w-full px-3 py-3 border-2 rounded-lg bg-white text-gray-600 font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-center text-lg",
              {
                "border-red-500": error,
                "border-gray-300": !error,
                "border-pink-500": focusedField === "month",
              }
            )}
            data-testid="month-input"
            ref={monthInputRef}
          />
        </div>

        {/* Year input */}
        <div className="relative min-w-0 flex-1">
          <Label className="block text-sm font-semibold text-gray-700 mb-2">Year</Label>
          <Input
            type="text"
            inputMode="numeric"
            min={minYear}
            max={maxYear}
            maxLength={4}
            enterKeyHint="done"
            value={year || ""}
            onChange={handleYearChange}
            onFocus={() => setFocusedField("year")}
            onBlur={handleYearBlur}
            onKeyDown={(e) => handleKeyDown(e, "year")}
            placeholder="YYYY"
            className={clsx(
              "w-full px-3 py-3 border-2 rounded-lg bg-white text-gray-600 font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-center text-lg",
              {
                "border-red-500": error,
                "border-gray-300": !error,
                "border-pink-500": focusedField === "year",
              }
            )}
            data-testid="year-input"
            ref={yearInputRef}
          />
        </div>
      </div>
    </Field>
  );
}
